import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";
import { inboxApi } from "@/slices/inbox/inboxApi";

export const documentTemplatesApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "documentTemplatesApi",
    baseUrl: "/api/document-templates",
  }),
  reducerPath: "documentTemplatesApi",
  tagTypes: ["Documents", "Document", "Document Template History"],
  endpoints: (builder) => ({
    getDocumentTemplates: builder.query<
      any,
      {
        include?: string;
        search?: string;
        nextId?: string;
        limit?: number;
        shareLevel?: string;
        status?: string; // Filter by status (e.g., "DRAFT,APPROVED" to exclude OCR templates)
      }
    >({
      query: (args) => {
        // Build query parameters dynamically
        const queryParams = new URLSearchParams();
        // Append parameters if they exist
        if (args.search) queryParams.append("query", args.search);
        if (args.include) queryParams.append("include", args.include);
        if (args.limit) queryParams.append("limit", args.limit.toString());
        if (args.nextId) queryParams.append("nextId", args.nextId);
        if (args.shareLevel) queryParams.append("shareLevel", args.shareLevel);
        if (args.status) queryParams.append("status", args.status);
        return `?${queryParams.toString()}`;
      },
      keepUnusedDataFor: 0,
      providesTags: [{ type: "Documents" }],
    }),
    getDocumentTemplatesWithFieldsById: builder.query<
      any,
      { id?: string; include?: string }
    >({
      query: (args) => {
        const queryParams = args.include ? `?include=${args.include}` : "";
        return `/${args.id}/fields${queryParams}`;
      },
      keepUnusedDataFor: 0,
      providesTags: [{ type: "Document" }],
    }),
    getDocumentTemplateHistory: builder.query<any, { id: string }>({
      query: (args) => `/${args.id}/history`,
      keepUnusedDataFor: 0,
      providesTags: [{ type: "Document Template History" }],
    }),
    getDocumentTemplateHistoryById: builder.query<
      any,
      { id: string; historyId?: string }
    >({
      query: (args) => `/${args.id}/history/${args.historyId}`,
      keepUnusedDataFor: 0,
    }),
    updateDocumentTemplateById: builder.mutation<
      any,
      { id: string; data: any }
    >({
      query: ({ id, data }) => ({
        url: `/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: [{ type: "Documents" }],
    }),
    patchDocumentTemplateById: builder.mutation<any, { id: string; data: any }>(
      {
        query: ({ id, data }) => ({
          url: `/${id}`,
          method: "PATCH",
          body: data,
        }),
        invalidatesTags: [{ type: "Documents" }],
        async onQueryStarted(_, { dispatch, queryFulfilled }) {
          await queryFulfilled;
          dispatch(inboxApi.util.invalidateTags(["Inbox"]));
        },
      }
    ),
    createDocumentTemplate: builder.mutation<
      any,
      {
        type?: string;
        name: string;
        folderName?: string;
        order?: number;
        version?: number;
      }
    >({
      query: ({ type, name, folderName, order }) => ({
        url: `/`,
        method: "POST",
        body: {
          name,
          type,
          folderName,
          order: order || 0, // TODO: what order do we put for templates not on a workflow?
          version: 0, // required by backend, always going to be 0 on first create
        },
      }),
      invalidatesTags: [{ type: "Documents" }],
    }),
    bulkRenameFolder: builder.mutation<
      any,
      { oldName: string; newName: string }
    >({
      query: ({ oldName, newName }) => ({
        url: `/folder-rename`,
        method: "POST",
        body: { oldName, newName },
      }),
      invalidatesTags: [{ type: "Documents" }],
    }),
    bulkDeleteFolder: builder.mutation<any, { folderName: string }>({
      query: ({ folderName }) => ({
        url: `/folder-delete`,
        method: "POST",
        body: { folderName },
      }),
      invalidatesTags: [{ type: "Documents" }],
    }),
    deleteDocumentTemplate: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Documents" }],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(inboxApi.util.invalidateTags(["Inbox"]));
      },
    }),
  }),
});

export const {
  useGetDocumentTemplatesQuery,
  useGetDocumentTemplatesWithFieldsByIdQuery,
  useGetDocumentTemplateHistoryQuery,
  useGetDocumentTemplateHistoryByIdQuery,
  useLazyGetDocumentTemplateHistoryByIdQuery,
  useUpdateDocumentTemplateByIdMutation,
  usePatchDocumentTemplateByIdMutation,
  useCreateDocumentTemplateMutation,
  useDeleteDocumentTemplateMutation,
  useBulkRenameFolderMutation,
  useBulkDeleteFolderMutation,
} = documentTemplatesApi;
